import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Dialog, DialogContent } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useNavigate } from "react-router-dom";
import {
  ApplicationType,
  date,
  entity_id,
  locationGeo,
  Platform,
} from "../Utils/Const";

import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";
import api from "../Utils/Api";

// static "old" PIN used because this is a first-time set (no existing PIN)
const OLD_PIN = "9999";

export default function SetRupayPinSetup({
  mobileNumber,
  sessionId,
  rupayRefNo,
  onSuccess,
  onBack,
  isFirstTimeSetup = true, 
}) {
  const ip = useIPAddress();
  const navigate = useNavigate();

  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [dialog, setDialog] = useState({
    open: false,
    success: false,
    message: "",
  });

  const isMatch = newPin.length === 4 && newPin === confirmPin;

  const handleSetPin = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      // 1) card status
      const cardRes = await api.post(`${baseapi}/getcardstatus`, {
        ReqType: "Jan_Aadhaar_eWallet_GetCardStatus",
        Mobilenumber: String(mobileNumber),
        SessionID: sessionId,
        ReferenceNumber: rupayRefNo,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });
      if (String(cardRes.data.Response_Code) !== "4001") {
        throw new Error(cardRes.data.Message || "Card status failed");
      }
      const { MaskedCard, EncKey } = cardRes.data;

      // 2) hex XOR – OLD PIN (static 9999)
      const { data: hexOld } = await api.post(
        `${baseapi}/hexXorgenerator`,
        { rpin: OLD_PIN, mobilenumber: mobileNumber },
        { responseType: "text" },
      );

      // 3) encrypt OLD PIN
      const encOldRes = await api.post(`${baseapi}/ewalletRupayEncryption3DES`, {
        ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
        Mobilenumber: String(mobileNumber),
        Enc_Key: EncKey,
        Hex_XOR_Value: hexOld,
        Date: date,
        SessionID: sessionId,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });
      if (String(encOldRes.data.Response_Code) !== "1000") {
        throw new Error(encOldRes.data.Message || "Encryption failed (old PIN)");
      }
      const oldEncryptedPin = encOldRes.data.RPIN;

      // 4) hex XOR – NEW PIN (the confirmed PIN entered by user)
      const { data: hexNew } = await api.post(
        `${baseapi}/hexXorgenerator`,
        { rpin: confirmPin, mobilenumber: mobileNumber },
        { responseType: "text" },
      );

      // 5) encrypt NEW PIN
      const encNewRes = await api.post(`${baseapi}/ewalletRupayEncryption3DES`, {
        ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
        Mobilenumber: String(mobileNumber),
        Enc_Key: EncKey,
        Hex_XOR_Value: hexNew,
        Date: date,
        SessionID: sessionId,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });
      if (String(encNewRes.data.Response_Code) !== "1000") {
        throw new Error(encNewRes.data.Message || "Encryption failed (new PIN)");
      }
      const newEncryptedPin = encNewRes.data.RPIN;

      // 6) change PIN  (old = static 9999, new = user's confirmed PIN)
      const finalRes = await api.post(`${baseapi}/eWalletRuPayChangePIN`, {
        ReqType: "Jan_Aadhaar_eWallet_RuPay_Change_PIN",
        Mobilenumber: String(mobileNumber),
        SessionID: sessionId,
        CardNumber: MaskedCard,
        R_PIN: oldEncryptedPin,
        New_PIN: newEncryptedPin,
        ReferenceNumber: rupayRefNo,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
        IsFirstTimePinSetup: isFirstTimeSetup, 
      });

      console.log("SetRupayPIN (change flow) final response:", finalRes.data);

      if (String(finalRes.data.Response_Code) === "5000") {
        setDialog({
          open: true,
          success: true,
          message: finalRes.data.Message || "Your RuPay card PIN has been created successfully.",
        });
      } else {
        setDialog({
          open: true,
          success: false,
          message: finalRes.data.Message || "Failed to set RuPay PIN",
        });
      }
    } catch (err) {
      console.error(err);
      setDialog({
        open: true,
        success: false,
        message: err.message || "Something went wrong while setting PIN",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    const wasSuccess = dialog.success;
    setDialog({ open: false, success: false, message: "" });
    if (wasSuccess) {
      onSuccess?.();
      navigate("/dashboard"); // adjust to your actual dashboard route
    }
  };

  return (
    <>
      <Box
        sx={{
          width: { xs: "90%", sm: "420px" },
          background: "#fff",
          borderRadius: 4,
          p: 4,
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Set RuPay PIN
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Create a 4-digit PIN for your RuPay card to complete setup.
        </Typography>

        <PinInput label="New RuPay PIN" value={newPin} setValue={setNewPin} show={show} setShow={setShow} />
        <PinInput label="Confirm RuPay PIN" value={confirmPin} setValue={setConfirmPin} show={show} setShow={setShow} />

        {confirmPin.length === 4 && !isMatch && (
          <Typography mt={1} fontSize={13} sx={{ color: "#dc2626" }}>
            PINs do not match.
          </Typography>
        )}

        {errorMsg && (
          <Typography mt={2} fontSize={13} textAlign="center" sx={{ color: "#dc2626", fontWeight: 500 }}>
            {errorMsg}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onBack}
            sx={{ height: 48, borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#334155" }}
          >
            Back
          </Button>
          <Button
            fullWidth
            disabled={!isMatch || loading}
            onClick={handleSetPin}
            sx={{
              height: 48,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              background: !isMatch || loading ? "#d1d5db" : "linear-gradient(135deg, #16a34a, #22c55e)",
              color: !isMatch || loading ? "#888" : "#fff",
            }}
          >
            {loading ? "Setting..." : "Set PIN"}
          </Button>
        </Box>
      </Box>

      {/* ── SUCCESS / FAILURE POPUP ── */}
      <Dialog
        open={dialog.open}
        onClose={handleDialogClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 4,
            boxShadow: "0px 20px 60px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", p: 4, minWidth: 320 }}>
          <Typography fontSize={20} fontWeight={700} sx={{ color: dialog.success ? "#16a34a" : "#dc2626" }}>
            {dialog.success ? "RuPay PIN Set" : "Action Failed"}
          </Typography>

          <Typography fontSize={14} mt={1.5} sx={{ color: "#6b7280", lineHeight: 1.6 }}>
            {dialog.message}
          </Typography>

          <Button
            variant="contained"
            onClick={handleDialogClose}
            sx={{
              mt: 3,
              px: 4,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: dialog.success ? "#16a34a" : "#dc2626",
              "&:hover": { backgroundColor: dialog.success ? "#15803d" : "#b91c1c" },
            }}
          >
            {dialog.success ? "Continue" : "Okay"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PinInput({ label, value, setValue, show, setShow }) {
  return (
    <Box mt={2}>
      <Typography fontSize={13}>{label}</Typography>
      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          border: "1px solid #22c55e",
          borderRadius: 2,
          px: 2,
          height: 48,
        }}
      >
        <input
          type={show ? "text" : "password"}
          value={value}
          maxLength={4}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
          style={{ border: "none", outline: "none", flex: 1, letterSpacing: "10px", fontSize: 20 }}
        />
        <IconButton onClick={() => setShow(!show)}>
          {show ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}